/**
 * Initial entitlement resolution for useSubscription — decides WHEN the app
 * may stop treating the user's entitlement as unknown (the `loading` that
 * PlanningGate reads), given two checks that now run in PARALLEL:
 *
 *   backend: GET /subscription/status (denormalized backend row)
 *   rc:      RevenueCat startup + customerInfo (native only; null elsewhere)
 *
 * Each promise resolves (never rejects) to { paid: boolean }.
 *
 *   - either source confirms paid  -> resolve immediately (paid UI may render);
 *   - FREE only once the backend has answered not-paid AND RevenueCat has
 *     answered not-paid, so a stale backend FREE can never flash a paying
 *     user as free while RevenueCat is still starting;
 *   - hang guard: if the backend said not-paid and RevenueCat has not answered
 *     within `rcWaitMs`, resolve FREE (RevenueCat confirming paid later still
 *     unlocks via applyCustomerInfo, exactly as before this change).
 *
 * Resolves to { source: 'rc_paid' | 'backend_paid' | 'free' | 'free_rc_timeout' }.
 */
export var RC_INITIAL_WAIT_MS = 8000;

export function resolveInitialEntitlement(backend, rc, rcWaitMs) {
  var waitMs = typeof rcWaitMs === 'number' ? rcWaitMs : RC_INITIAL_WAIT_MS;
  return new Promise(function (resolve) {
    var done = false;
    var backendAnswer = null; // null = pending, else { paid }
    var rcAnswer = rc ? null : { paid: false };
    var timer = null;

    function finish(source) {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      resolve({ source: source });
    }

    function evaluate() {
      if (done) return;
      if (rcAnswer && rcAnswer.paid) return finish('rc_paid');
      if (backendAnswer && backendAnswer.paid) return finish('backend_paid');
      if (backendAnswer && rcAnswer) return finish('free');
      if (backendAnswer && !rcAnswer && !timer) {
        timer = setTimeout(function () { finish('free_rc_timeout'); }, waitMs);
      }
    }

    Promise.resolve(backend).then(
      function (r) { backendAnswer = { paid: !!(r && r.paid) }; evaluate(); },
      function () { backendAnswer = { paid: false }; evaluate(); }
    );
    if (rc) {
      Promise.resolve(rc).then(
        function (r) { rcAnswer = { paid: !!(r && r.paid) }; evaluate(); },
        function () { rcAnswer = { paid: false }; evaluate(); }
      );
    }
  });
}
